import { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { AuthFlowContent } from '../contents/auth-flow.content';
import { useAuthFlow, useIsMobile, useIsRegisteredUser } from '@/hooks';
import SignupView from '../views/signup.view';
import { cn } from '@/utils';
import { useSelector } from 'react-redux';
import { useGetProfileQuery } from '@/store/api';
import { skipToken } from '@reduxjs/toolkit/query';

interface AuthModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function AuthModal({ open, onOpenChange }: AuthModalProps) {
	const isMobile = useIsMobile();
	const [isSignUpStep, setIsSignUpStep] = useState(false);
	const [signupEmail, setSignupEmail] = useState('');
	const scrollPositionRef = useRef<{ left: number; top: number } | null>(null);

	const mode = useSelector((state: any) => state.authModal.mode);
	const isRegisteredUser = useIsRegisteredUser();
	const { data: user } = useGetProfileQuery(isRegisteredUser ? undefined : skipToken);

	const closeAuthModal = () => {
		onOpenChange(false);
	};

	const captureScrollPosition = useCallback(() => {
		if (typeof window === 'undefined') return;

		scrollPositionRef.current = {
			left: window.scrollX,
			top: window.scrollY,
		};
	}, []);

	const restoreScrollPosition = useCallback((clearAfterRestore = false) => {
		const scrollPosition = scrollPositionRef.current;
		if (!scrollPosition || typeof window === 'undefined') return undefined;

		const restore = () => {
			window.scrollTo({
				left: scrollPosition.left,
				top: scrollPosition.top,
				behavior: 'auto',
			});
		};

		restore();
		window.requestAnimationFrame(() => {
			restore();
			window.requestAnimationFrame(restore);
		});

		const restoreTimer = window.setTimeout(restore, 100);
		const finalRestoreTimer = window.setTimeout(() => {
			restore();
			if (clearAfterRestore) {
				scrollPositionRef.current = null;
			}
		}, 550);

		return () => {
			window.clearTimeout(restoreTimer);
			window.clearTimeout(finalRestoreTimer);
		};
	}, []);

	useEffect(() => {
		if (open && !scrollPositionRef.current) {
			captureScrollPosition();
		}
	}, [open, captureScrollPosition]);

	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			if (nextOpen) {
				captureScrollPosition();
			}
			onOpenChange(nextOpen);
		},
		[captureScrollPosition, onOpenChange]
	);

	const handleCloseAutoFocus = useCallback(
		(event: Event) => {
			event.preventDefault();
			restoreScrollPosition();
		},
		[restoreScrollPosition]
	);

	const {
		email,
		setEmail,
		error,
		setError,
		isLoading,
		isWTN,
		isVerifyingOtp,
		setIsVerifyingOtp,
		handleRequestOtp,
		handleVerifyOtp,
		handleResendOtp,
		onGoogleMobileAuth,
		onAppleMobileAuth,
		onGoogleWebAuth,
		onGoogleExternalBrowserHandoff,
		onCopyExternalBrowserLink,
		onGoogleWebAuthResponse,
		onAppleWebAuth,
		isXInAppBrowser,
		xInAppBrowserPlatform,
		showXBrowserHandoff,
		redirectUrl,
	} = useAuthFlow({
		onClose: closeAuthModal,
		onSignUpStep: (emailAddress) => {
			setSignupEmail(emailAddress);
			setIsSignUpStep(true);
		},
	});

	useEffect(() => {
		if (open && mode === 'setup' && user) {
			setIsSignUpStep(true);
			setSignupEmail(user.email || '');
		} else if (!open) {
			setIsVerifyingOtp(false);
			setIsSignUpStep(false);
			setSignupEmail('');
		}
	}, [open, mode, user, setIsVerifyingOtp]);

	useEffect(() => {
		if (open || !scrollPositionRef.current) return undefined;

		return restoreScrollPosition(true);
	}, [open, restoreScrollPosition]);

	const isWebSidebar = isSignUpStep && !isMobile;

	return (
		<Drawer fixed open={open} onOpenChange={handleOpenChange}>
			<DrawerContent
				hideHandle
				onCloseAutoFocus={handleCloseAutoFocus}
				className={cn(
					// Never taller than the visible screen (in-app browsers like Telegram's leave little
					// room): the sheet keeps its 556px design height where it fits and its content
					// scrolls otherwise. The sheet itself stays overflow-hidden: the drawer library hangs
					// a 200%-tall ::after under it, which would become scrollable empty space.
					"bg-[#101423] p-4 pb-[max(1rem,env(safe-area-inset-bottom))] font-['DM_Sans'] h-auto! min-h-[min(556px,92dvh)] max-h-[92dvh] rounded-t-[38px] transition-[min-height] duration-500 ease-in-out overflow-hidden bottom-0!",
					isWebSidebar
						? 'lg:bottom-auto! lg:inset-x-auto! lg:left-auto! lg:right-0! lg:top-0 lg:bottom-0 lg:h-full lg:min-h-screen lg:max-h-screen lg:w-[501px] lg:ml-0 lg:mt-0 lg:rounded-none lg:rounded-r-none lg:animate-in lg:slide-in-from-right lg:duration-600 lg:ease-out'
						: 'lg:bottom-auto! lg:inset-x-auto! lg:top-1/2 lg:left-1/2! lg:right-auto! lg:ml-[-196.5px] lg:mt-[-278px] lg:max-w-[393px] lg:w-full lg:rounded-[38px] lg:max-h-[90vh]'
				)}
			>
				{isWebSidebar ? (
					<>
						<button
							type="button"
							onClick={closeAuthModal}
							className="absolute left-6 top-6 h-10 w-10 flex items-center justify-center rounded-full bg-white/10 text-white/60 transition-all hover:text-white hover:bg-white/20 z-50 cursor-pointer"
							aria-label="Close setup"
						>
							<X className="h-5 w-5" strokeWidth={1.5} />
						</button>
						<div className="w-full h-full overflow-y-auto hide-scrollbar animate-in slide-in-from-right duration-600 ease-out">
							<SignupView
								emailFromUrl={signupEmail}
								redirectUrl={redirectUrl || undefined}
								isModal={true}
								onClose={closeAuthModal}
							/>
						</div>
					</>
				) : (
					<div className="min-h-0 flex-1 overflow-y-auto overscroll-contain hide-scrollbar">
						<AuthFlowContent
							email={email}
							setEmail={setEmail}
							error={error}
							setError={setError}
							isLoading={isLoading}
							isWTN={isWTN}
							isVerifyingOtp={isVerifyingOtp}
							setIsVerifyingOtp={setIsVerifyingOtp}
							handleRequestOtp={handleRequestOtp}
							handleVerifyOtp={handleVerifyOtp}
							handleResendOtp={handleResendOtp}
							onGoogleMobileAuth={onGoogleMobileAuth}
							onAppleMobileAuth={onAppleMobileAuth}
							onGoogleWebAuth={onGoogleWebAuth}
							onGoogleExternalBrowserHandoff={onGoogleExternalBrowserHandoff}
							onCopyExternalBrowserLink={onCopyExternalBrowserLink}
							onGoogleWebAuthResponse={onGoogleWebAuthResponse}
							onAppleWebAuth={onAppleWebAuth}
							isXInAppBrowser={isXInAppBrowser}
							xInAppBrowserPlatform={xInAppBrowserPlatform}
							showXBrowserHandoff={showXBrowserHandoff}
							onClose={closeAuthModal}
							onOpenChange={onOpenChange}
							autoFocusEmail={!isMobile}
						/>
					</div>
				)}
			</DrawerContent>
		</Drawer>
	);
}
